import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Header,
    SpaceBetween,
    Cards,
    Box,
    StatusIndicator,
    Button,
    Modal,
    FormField,
    Textarea,
    Alert,
    Input,
    KeyValuePairs,
    ExpandableSection
} from '@cloudscape-design/components';
import { trading212CredentialsApi, apiCredentialsApi } from '../../client/clients';
import {
    ApiCredentialRequest,
    ApiCredentialRequestCredentialTypeEnum,
    ApiCredentialResponse,
    HealthCheckPathCredentialKeyEnum,
    Trading212ApiCredentialUpdateRequestKeyEnum,
    Trading212CredentialUpdateRequest,
    Trading212CredentialUpdateRequestKeyEnum
} from "../../api";
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFlashbar } from '../../context/FlashbarContext';
import { classifyError } from '../../util/errorClassifier';

export const CredentialType = ApiCredentialRequestCredentialTypeEnum;
export type CredentialType = ApiCredentialRequestCredentialTypeEnum;

/** SeekingAlpha credential types that carry a buyPercentage field. */
const SEEKING_ALPHA_TYPES_WITH_BUY_PCT = new Set<string>([
    CredentialType.SeekingAlphaPro,
    CredentialType.SeekingAlphaPremium,
]);

/** Quick-pick values shown as inline buttons on SeekingAlpha cards. */
const BUY_PCT_QUICK_PICKS: ReadonlyArray<number> = [0, 0.25, 0.5, 0.75, 1.0];

/** Credential keys that should use a password input (short, opaque tokens). */
const PASSWORD_INPUT_KEYS = new Set<string>([
    'POLYGON_API_KEY',
    'TRADING212_API_KEY',
    'TRADING212_API_SECRET',
    'Trading212ApiKey',
    'ALPHA_WORKER_API_KEY',
]);

interface CredentialHealthStatus {
    key: CredentialType | Trading212CredentialUpdateRequestKeyEnum | string;
    keyName: string;
    isHealthy: boolean;
    error?: string;
    provider: 'SeekingAlpha' | 'Trading212' | 'API';
    isRefreshing?: boolean;
    banner?: { type: 'success' | 'error', message: string } | null;
    healthChecked?: boolean;
    hasValue?: boolean;
    buyPercentage?: number;
    createdAt?: string;
    updatedAt?: string;
}

interface UpdateModalState {
    isVisible: boolean;
    credentialKey?: CredentialType | Trading212CredentialUpdateRequestKeyEnum | string;
    credentialName?: string;
    value: string;
    provider?: 'SeekingAlpha' | 'Trading212' | 'API';
}

interface PublicApiModalState {
    isVisible: boolean;
    apiKey: string;
    apiSecret: string;
    accountId: 'primary' | 'ina';
}

/** Per-card inline buy-percentage editor state, keyed by credential key. */
interface BuyPctEditorState {
    inputValue: string;
    error: string;
    saving: boolean;
}

export function CredentialsManagementPage() {
    useDocumentTitle('Credentials');
    const flashbar = useFlashbar();
    const [seekingAlphaStatuses, setSeekingAlphaStatuses] = useState<CredentialHealthStatus[]>([]);
    const [trading212Statuses, setTrading212Statuses] = useState<CredentialHealthStatus[]>([]);
    const [apiCredentialStatuses, setApiCredentialStatuses] = useState<CredentialHealthStatus[]>([]);
    const [refreshing, setRefreshing] = useState(false);
    const [seekingAlphaRefreshing, setSeekingAlphaRefreshing] = useState(false);
    const [trading212Refreshing, setTrading212Refreshing] = useState(false);
    const [apiCredentialRefreshing, setApiCredentialRefreshing] = useState(false);
    const [seekingAlphaBanner, setSeekingAlphaBanner] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [trading212Banner, setTrading212Banner] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [apiCredentialBanner, setApiCredentialBanner] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [updateModal, setUpdateModal] = useState<UpdateModalState>({
        isVisible: false,
        value: ''
    });
    const [updating, setUpdating] = useState(false);
    const [updateResult, setUpdateResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [publicApiModal, setPublicApiModal] = useState<PublicApiModalState>({
        isVisible: false,
        apiKey: '',
        apiSecret: '',
        accountId: 'primary',
    });
    const [publicApiUpdating, setPublicApiUpdating] = useState(false);
    const [publicApiUpdateResult, setPublicApiUpdateResult] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    /**
     * Inline buy-percentage editor state, keyed by credential key string.
     * Separate from the main credential status so it does not re-render cards
     * on every keystroke.
     */
    const [buyPctEditors, setBuyPctEditors] = useState<Record<string, BuyPctEditorState>>({});

    const getBuyPctEditor = (key: string): BuyPctEditorState =>
        buyPctEditors[key] ?? { inputValue: '', error: '', saving: false };

    const setBuyPctEditor = (key: string, patch: Partial<BuyPctEditorState>) =>
        setBuyPctEditors(prev => ({
            ...prev,
            [key]: { ...getBuyPctEditor(key), ...patch }
        }));

    // Helper function to fetch SeekingAlpha credentials via the unified API
    const fetchSeekingAlphaCredentials = useCallback(async (): Promise<CredentialHealthStatus[]> => {
        try {
            const [proMetadata, premiumMetadata] = await Promise.all([
                apiCredentialsApi.getCredentialMetadata(CredentialType.SeekingAlphaPro),
                apiCredentialsApi.getCredentialMetadata(CredentialType.SeekingAlphaPremium)
            ]);

            return [
                {
                    key: CredentialType.SeekingAlphaPro,
                    keyName: 'SeekingAlpha Pro',
                    isHealthy: proMetadata.data.hasValue || false,
                    hasValue: proMetadata.data.hasValue || false,
                    buyPercentage: proMetadata.data.buyPercentage,
                    createdAt: proMetadata.data.createdAt,
                    updatedAt: proMetadata.data.updatedAt,
                    error: proMetadata.data.hasValue ? undefined : 'No value configured',
                    provider: 'SeekingAlpha',
                    healthChecked: true
                },
                {
                    key: CredentialType.SeekingAlphaPremium,
                    keyName: 'SeekingAlpha Premium',
                    isHealthy: premiumMetadata.data.hasValue || false,
                    hasValue: premiumMetadata.data.hasValue || false,
                    buyPercentage: premiumMetadata.data.buyPercentage,
                    createdAt: premiumMetadata.data.createdAt,
                    updatedAt: premiumMetadata.data.updatedAt,
                    error: premiumMetadata.data.hasValue ? undefined : 'No value configured',
                    provider: 'SeekingAlpha',
                    healthChecked: true
                }
            ];
        } catch (error: unknown) {
            console.error('Error fetching SeekingAlpha credentials:', error);
            return [
                {
                    key: CredentialType.SeekingAlphaPro,
                    keyName: 'SeekingAlpha Pro',
                    isHealthy: false,
                    error: 'Failed to fetch status',
                    provider: 'SeekingAlpha',
                    healthChecked: true
                },
                {
                    key: CredentialType.SeekingAlphaPremium,
                    keyName: 'SeekingAlpha Premium',
                    isHealthy: false,
                    error: 'Failed to fetch status',
                    provider: 'SeekingAlpha',
                    healthChecked: true
                }
            ];
        }
    }, []);

    // Helper function to check health of a Trading212 credential
    const checkTrading212CredentialHealth = useCallback(async (key: HealthCheckPathCredentialKeyEnum): Promise<CredentialHealthStatus> => {
        const getKeyName = (k: string) => {
            switch (k) {
                case 'Trading212Isa': return 'Trading 212 ISA';
                case 'Trading212Cfd': return 'Trading 212 CFD';
                case 'Trading212ApiKey': return 'Trading 212 API key';
                default: return k;
            }
        };

        try {
            await trading212CredentialsApi.healthCheckPath(key);
            return {
                key: key as Trading212CredentialUpdateRequestKeyEnum,
                keyName: getKeyName(key),
                isHealthy: true,
                provider: 'Trading212',
                healthChecked: true
            };
        } catch (error: unknown) {
            console.error(`Health check failed for Trading212 credential ${key}:`, error);
            return {
                key: key as Trading212CredentialUpdateRequestKeyEnum,
                keyName: getKeyName(key),
                isHealthy: false,
                error: error instanceof Error ? error.message : 'Health check failed',
                provider: 'Trading212',
                healthChecked: true
            };
        }
    }, []);

    // Helper function to check health of the Trading212 Public API credentials for a given account
    const checkPublicApiHealth = useCallback(async (accountId: 'primary' | 'ina' = 'primary'): Promise<CredentialHealthStatus> => {
        const key = accountId === 'ina' ? 'Trading212PublicApiIna' : 'Trading212PublicApi';
        const keyName = accountId === 'ina' ? 'Public API (Ina)' : 'Public API';
        try {
            const response = await trading212CredentialsApi.apiHealthCheck(accountId);
            const summary = response.data;
            return {
                key,
                keyName,
                isHealthy: true,
                hasValue: true,
                error: undefined,
                provider: 'Trading212',
                healthChecked: true,
                banner: {
                    type: 'success',
                    message: summary.currency
                        ? `Connected — ${summary.currency} ${summary.cash?.availableToTrade != null ? Number(summary.cash.availableToTrade).toFixed(2) : 'N/A'} available`
                        : 'Connected'
                }
            };
        } catch (error: unknown) {
            console.error(`Health check failed for Trading212 Public API (${accountId}):`, error);
            return {
                key,
                keyName,
                isHealthy: false,
                hasValue: false,
                error: error instanceof Error ? error.message : 'Health check failed',
                provider: 'Trading212',
                healthChecked: true
            };
        }
    }, []);

    const checkAllTrading212CredentialsHealth = useCallback(async (): Promise<CredentialHealthStatus[]> => {
        const keys: HealthCheckPathCredentialKeyEnum[] = [
            HealthCheckPathCredentialKeyEnum.Trading212Isa,
            HealthCheckPathCredentialKeyEnum.Trading212Cfd,
            HealthCheckPathCredentialKeyEnum.Trading212ApiKey
        ];
        const [legacyStatuses, publicApiStatus, inaPublicApiStatus] = await Promise.all([
            Promise.all(keys.map(key => checkTrading212CredentialHealth(key))),
            checkPublicApiHealth('primary'),
            checkPublicApiHealth('ina')
        ]);
        return [...legacyStatuses, publicApiStatus, inaPublicApiStatus];
    }, [checkTrading212CredentialHealth, checkPublicApiHealth]);

    // Helper function to update a SeekingAlpha credential
    const updateSeekingAlphaCredential = async (key: CredentialType, value: string, buyPercentage?: number): Promise<boolean> => {
        try {
            const request: ApiCredentialRequest = {
                credentialType: key,
                value: value || undefined,
                buyPercentage
            };
            await apiCredentialsApi.upsertCredential(request);
            return true;
        } catch (error) {
            console.error('Error updating SeekingAlpha credential:', error);
            return false;
        }
    };

    // Helper function to update a Trading212 credential
    const updateTrading212Credential = async (key: Trading212CredentialUpdateRequestKeyEnum, value: string): Promise<boolean> => {
        try {
            const request: Trading212CredentialUpdateRequest = {
                key,
                value
            };
            await trading212CredentialsApi.updateCredential(request);
            return true;
        } catch (error) {
            console.error('Error updating Trading212 credential:', error);
            return false;
        }
    };

    const API_CREDENTIAL_KEYS: ReadonlyArray<{ key: string; label: string }> = [
        { key: 'POLYGON_API_KEY', label: 'Polygon API key' },
        { key: 'ALPHA_WORKER_API_KEY', label: 'Alpha Worker API key' },
    ];

    // Helper function to fetch all API credentials
    const fetchApiCredentials = useCallback(async (): Promise<CredentialHealthStatus[]> => {
        try {
            const response = await apiCredentialsApi.listCredentials();
            const fromDb = new Map(
                response.data.map((c: ApiCredentialResponse) => [c.credentialKey ?? '', c])
            );
            return API_CREDENTIAL_KEYS.map(({ key, label }) => {
                const entry = fromDb.get(key);
                return {
                    key,
                    keyName: label,
                    isHealthy: entry?.hasValue || false,
                    hasValue: entry?.hasValue || false,
                    createdAt: entry?.createdAt,
                    updatedAt: entry?.updatedAt,
                    error: entry?.hasValue ? undefined : 'No value configured',
                    provider: 'API' as const,
                    healthChecked: true
                };
            });
        } catch (error: unknown) {
            console.error('Error fetching API credentials:', error);
            return API_CREDENTIAL_KEYS.map(({ key, label }) => ({
                key,
                keyName: label,
                isHealthy: false,
                hasValue: false,
                error: 'Failed to fetch status',
                provider: 'API' as const,
                healthChecked: true
            }));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper function to update an API credential
    const updateApiCredential = async (key: string, value: string): Promise<boolean> => {
        try {
            const request: ApiCredentialRequest = {
                credentialType: key as 'POLYGON_API_KEY',
                value
            };
            await apiCredentialsApi.upsertCredential(request);
            return true;
        } catch (error) {
            console.error('Error updating API credential:', error);
            return false;
        }
    };

    /**
     * Save only the buyPercentage for a SeekingAlpha credential.
     * Fires a Flashbar message with old → new values.
     */
    const saveBuyPercentage = useCallback(async (
        credentialKey: string,
        credentialName: string,
        oldValue: number | undefined,
        newValue: number
    ): Promise<void> => {
        setBuyPctEditor(credentialKey, { saving: true, error: '' });
        try {
            const request: ApiCredentialRequest = {
                credentialType: credentialKey as CredentialType,
                buyPercentage: newValue
            };
            await apiCredentialsApi.upsertCredential(request);

            // Update local state so the card reflects the new value immediately.
            setSeekingAlphaStatuses(prev => prev.map(s =>
                s.key === credentialKey ? { ...s, buyPercentage: newValue } : s
            ));

            const oldLabel = oldValue !== undefined ? oldValue.toFixed(2) : 'not set';
            flashbar.push({
                type: 'success',
                header: `Buy allocation updated`,
                content: `Buy allocation for ${credentialName}: ${oldLabel} → ${newValue.toFixed(2)}`,
            });
            // Clear the free-form input after successful save.
            setBuyPctEditor(credentialKey, { inputValue: '', saving: false, error: '' });
        } catch (err) {
            console.error('Error saving buy percentage:', err);
            setBuyPctEditor(credentialKey, { saving: false, error: 'Failed to save. Please try again.' });
        }
    }, [flashbar]); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize credential boxes without making API calls
    useEffect(() => {
        const initialSeekingAlphaStatuses: CredentialHealthStatus[] = [
            {
                key: CredentialType.SeekingAlphaPro,
                keyName: 'SeekingAlpha Pro',
                isHealthy: false,
                provider: 'SeekingAlpha',
                healthChecked: false
            },
            {
                key: CredentialType.SeekingAlphaPremium,
                keyName: 'SeekingAlpha Premium',
                isHealthy: false,
                provider: 'SeekingAlpha',
                healthChecked: false
            }
        ];

        const initialTrading212Statuses: CredentialHealthStatus[] = [
            {
                key: 'Trading212Isa' as Trading212CredentialUpdateRequestKeyEnum,
                keyName: 'Trading 212 ISA',
                isHealthy: false,
                provider: 'Trading212',
                healthChecked: false
            },
            {
                key: 'Trading212Cfd' as Trading212CredentialUpdateRequestKeyEnum,
                keyName: 'Trading 212 CFD',
                isHealthy: false,
                provider: 'Trading212',
                healthChecked: false
            },
            {
                key: 'Trading212ApiKey' as Trading212CredentialUpdateRequestKeyEnum,
                keyName: 'Trading 212 API key',
                isHealthy: false,
                provider: 'Trading212',
                healthChecked: false
            },
            {
                key: 'Trading212PublicApi',
                keyName: 'Public API',
                isHealthy: false,
                provider: 'Trading212',
                healthChecked: false,
                hasValue: false,
                error: undefined
            },
            {
                key: 'Trading212PublicApiIna',
                keyName: 'Public API (Ina)',
                isHealthy: false,
                provider: 'Trading212',
                healthChecked: false,
                hasValue: false,
                error: undefined
            }
        ];

        const initialApiCredentialStatuses: CredentialHealthStatus[] = [
            {
                key: 'POLYGON_API_KEY',
                keyName: 'Polygon API key',
                isHealthy: false,
                provider: 'API',
                healthChecked: false
            },
            {
                key: 'ALPHA_WORKER_API_KEY',
                keyName: 'Alpha Worker API key',
                isHealthy: false,
                provider: 'API',
                healthChecked: false
            }
        ];

        setSeekingAlphaStatuses(initialSeekingAlphaStatuses);
        setTrading212Statuses(initialTrading212Statuses);
        setApiCredentialStatuses(initialApiCredentialStatuses);
    }, []);


    const refreshHealthStatus = async () => {
        setRefreshing(true);
        try {
            const [saStatuses, t212Statuses, apiStatuses] = await Promise.all([
                fetchSeekingAlphaCredentials(),
                checkAllTrading212CredentialsHealth(),
                fetchApiCredentials()
            ]);
            setSeekingAlphaStatuses(saStatuses);
            setTrading212Statuses(t212Statuses);
            setApiCredentialStatuses(apiStatuses);
        } catch (error) {
            console.error('Error refreshing health status:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const refreshSeekingAlphaHealth = async () => {
        setSeekingAlphaRefreshing(true);
        setSeekingAlphaBanner(null);
        try {
            const saStatuses = await fetchSeekingAlphaCredentials();
            setSeekingAlphaStatuses(saStatuses);
            setSeekingAlphaBanner({ type: 'success', message: 'SeekingAlpha health status updated' });
        } catch (error) {
            console.error('Error refreshing SeekingAlpha health status:', error);
            const classified = classifyError(error);
            setSeekingAlphaBanner({ type: 'error', message: classified.userMessage });
        } finally {
            setSeekingAlphaRefreshing(false);
        }
    };

    const refreshTrading212Health = async () => {
        setTrading212Refreshing(true);
        setTrading212Banner(null);
        try {
            const t212Statuses = await checkAllTrading212CredentialsHealth();
            setTrading212Statuses(t212Statuses);
            setTrading212Banner({ type: 'success', message: 'Trading212 health status updated' });
        } catch (error) {
            console.error('Error refreshing Trading212 health status:', error);
            const classified = classifyError(error);
            setTrading212Banner({ type: 'error', message: classified.userMessage });
        } finally {
            setTrading212Refreshing(false);
        }
    };

    const refreshApiCredentialHealth = async () => {
        setApiCredentialRefreshing(true);
        setApiCredentialBanner(null);
        try {
            const apiStatuses = await fetchApiCredentials();
            setApiCredentialStatuses(apiStatuses);
            setApiCredentialBanner({ type: 'success', message: 'API credentials status updated' });
        } catch (error) {
            console.error('Error refreshing API credentials status:', error);
            const classified = classifyError(error);
            setApiCredentialBanner({ type: 'error', message: classified.userMessage });
        } finally {
            setApiCredentialRefreshing(false);
        }
    };

    const refreshIndividualSeekingAlphaCredential = async (credentialKey: CredentialType) => {
        setSeekingAlphaStatuses(prev => prev.map(status =>
            status.key === credentialKey
                ? { ...status, isRefreshing: true, banner: null }
                : status
        ));

        try {
            const metadata = await apiCredentialsApi.getCredentialMetadata(credentialKey);

            setSeekingAlphaStatuses(prev => prev.map(status =>
                status.key === credentialKey
                    ? {
                        ...status,
                        isHealthy: metadata.data.hasValue || false,
                        hasValue: metadata.data.hasValue || false,
                        buyPercentage: metadata.data.buyPercentage,
                        createdAt: metadata.data.createdAt,
                        updatedAt: metadata.data.updatedAt,
                        error: metadata.data.hasValue ? undefined : 'No value configured',
                        isRefreshing: false,
                        healthChecked: true,
                        banner: { type: 'success', message: `${status.keyName} status updated` }
                    }
                    : status
            ));
        } catch (error) {
            console.error(`Error refreshing individual SeekingAlpha credential ${credentialKey}:`, error);
            setSeekingAlphaStatuses(prev => prev.map(status =>
                status.key === credentialKey
                    ? {
                        ...status,
                        isRefreshing: false,
                        banner: { type: 'error', message: `Failed to refresh ${status.keyName}` }
                    }
                    : status
            ));
        }
    };

    const refreshIndividualTrading212Credential = async (credentialKey: Trading212CredentialUpdateRequestKeyEnum | string) => {
        setTrading212Statuses(prev => prev.map(status =>
            status.key === credentialKey
                ? { ...status, isRefreshing: true, banner: null }
                : status
        ));

        try {
            let credentialHealth: CredentialHealthStatus;
            if (credentialKey === 'Trading212PublicApi') {
                credentialHealth = await checkPublicApiHealth('primary');
            } else if (credentialKey === 'Trading212PublicApiIna') {
                credentialHealth = await checkPublicApiHealth('ina');
            } else {
                credentialHealth = await checkTrading212CredentialHealth(credentialKey as HealthCheckPathCredentialKeyEnum);
            }
            setTrading212Statuses(prev => prev.map(status =>
                status.key === credentialKey
                    ? {
                        ...credentialHealth,
                        isRefreshing: false,
                        banner: credentialHealth.banner ?? { type: 'success', message: `${credentialHealth.keyName} health status updated` }
                    }
                    : status
            ));
        } catch (error) {
            console.error(`Error refreshing individual Trading212 credential ${credentialKey}:`, error);
            setTrading212Statuses(prev => prev.map(status =>
                status.key === credentialKey
                    ? {
                        ...status,
                        isRefreshing: false,
                        banner: { type: 'error', message: `Failed to refresh ${status.keyName}` }
                    }
                    : status
            ));
        }
    };

    const refreshIndividualApiCredential = async (credentialKey: string) => {
        setApiCredentialStatuses(prev => prev.map(status =>
            status.key === credentialKey
                ? { ...status, isRefreshing: true, banner: null }
                : status
        ));

        try {
            const allCredentials = await fetchApiCredentials();
            const updatedCredential = allCredentials.find(c => c.key === credentialKey);

            if (updatedCredential) {
                setApiCredentialStatuses(prev => prev.map(status =>
                    status.key === credentialKey
                        ? {
                            ...updatedCredential,
                            isRefreshing: false,
                            banner: { type: 'success', message: `${updatedCredential.keyName} status updated` }
                        }
                        : status
                ));
            } else {
                throw new Error('Credential not found');
            }
        } catch (error) {
            console.error(`Error refreshing individual API credential ${credentialKey}:`, error);
            setApiCredentialStatuses(prev => prev.map(status =>
                status.key === credentialKey
                    ? {
                        ...status,
                        isRefreshing: false,
                        banner: { type: 'error', message: `Failed to refresh ${status.keyName}` }
                    }
                    : status
            ));
        }
    };

    const openUpdateModal = (credential: CredentialHealthStatus) => {
        if (credential.key === 'Trading212PublicApi') {
            setPublicApiModal({ isVisible: true, apiKey: '', apiSecret: '', accountId: 'primary' });
            setPublicApiUpdateResult(null);
            return;
        }
        if (credential.key === 'Trading212PublicApiIna') {
            setPublicApiModal({ isVisible: true, apiKey: '', apiSecret: '', accountId: 'ina' });
            setPublicApiUpdateResult(null);
            return;
        }
        setUpdateModal({
            isVisible: true,
            credentialKey: credential.key,
            credentialName: credential.keyName,
            provider: credential.provider,
            value: '',
        });
        setUpdateResult(null);
    };

    const closePublicApiModal = () => {
        setPublicApiModal({ isVisible: false, apiKey: '', apiSecret: '', accountId: 'primary' });
        setPublicApiUpdateResult(null);
    };

    const handleUpdatePublicApiCredential = async () => {
        if (!publicApiModal.apiKey.trim() && !publicApiModal.apiSecret.trim()) {
            setPublicApiUpdateResult({ type: 'error', message: 'Enter at least one field to update' });
            return;
        }
        setPublicApiUpdating(true);
        const isIna = publicApiModal.accountId === 'ina';
        const apiKeyEnum = isIna
            ? Trading212ApiCredentialUpdateRequestKeyEnum.InaApiKey
            : Trading212ApiCredentialUpdateRequestKeyEnum.ApiKey;
        const apiSecretEnum = isIna
            ? Trading212ApiCredentialUpdateRequestKeyEnum.InaApiSecret
            : Trading212ApiCredentialUpdateRequestKeyEnum.ApiSecret;
        const statusKey = isIna ? 'Trading212PublicApiIna' : 'Trading212PublicApi';
        const accountLabel = isIna ? 'Trading212 Public API (Ina)' : 'Trading212 Public API';
        try {
            if (publicApiModal.apiKey.trim()) {
                await trading212CredentialsApi.updateApiCredential({
                    key: apiKeyEnum,
                    value: publicApiModal.apiKey.trim()
                });
            }
            if (publicApiModal.apiSecret.trim()) {
                await trading212CredentialsApi.updateApiCredential({
                    key: apiSecretEnum,
                    value: publicApiModal.apiSecret.trim()
                });
            }
            setPublicApiUpdateResult({ type: 'success', message: 'Credentials updated successfully' });
            flashbar.push({
                type: 'success',
                header: `${accountLabel} updated`,
                content: 'Use the Verify button to test the new credentials.',
            });
            setTrading212Statuses(prev => prev.map(status =>
                status.key === statusKey
                    ? { ...status, hasValue: true, error: undefined }
                    : status
            ));
            setTimeout(() => { closePublicApiModal(); }, 1500);
        } catch (error) {
            setPublicApiUpdateResult({ type: 'error', message: 'Failed to update credentials' });
            flashbar.push({
                type: 'error',
                header: `${accountLabel} update failed`,
                content: 'An unexpected error occurred. Check the network tab for details.',
            });
        } finally {
            setPublicApiUpdating(false);
        }
    };

    const closeUpdateModal = () => {
        setUpdateModal({
            isVisible: false,
            value: '',
        });
        setUpdateResult(null);
    };

    const handleUpdateCredential = async () => {
        if (!updateModal.credentialKey || !updateModal.provider) {
            setUpdateResult({ type: 'error', message: 'Invalid credential configuration' });
            return;
        }

        if (!updateModal.value.trim()) {
            setUpdateResult({ type: 'error', message: 'Please enter a valid credential value' });
            return;
        }

        setUpdating(true);
        try {
            let success = false;
            if (updateModal.provider === 'SeekingAlpha') {
                success = await updateSeekingAlphaCredential(
                    updateModal.credentialKey as CredentialType,
                    updateModal.value.trim()
                );
            } else if (updateModal.provider === 'Trading212') {
                success = await updateTrading212Credential(
                    updateModal.credentialKey as Trading212CredentialUpdateRequestKeyEnum,
                    updateModal.value.trim()
                );
            } else if (updateModal.provider === 'API') {
                success = await updateApiCredential(
                    updateModal.credentialKey as string,
                    updateModal.value.trim()
                );
            }

            if (success) {
                setUpdateResult({ type: 'success', message: 'Credential updated successfully' });
                flashbar.push({
                    type: 'success',
                    header: `${updateModal.credentialName ?? 'Credential'} updated`,
                    content: 'Use the Verify button to re-check the credential\'s status.',
                });

                if (updateModal.provider === 'SeekingAlpha') {
                    setSeekingAlphaStatuses(prev => prev.map(status =>
                        status.key === updateModal.credentialKey
                            ? { ...status, isHealthy: true, hasValue: true, error: undefined }
                            : status
                    ));
                } else if (updateModal.provider === 'Trading212') {
                    setTrading212Statuses(prev => prev.map(status =>
                        status.key === updateModal.credentialKey
                            ? { ...status, isHealthy: true, error: undefined }
                            : status
                    ));
                } else if (updateModal.provider === 'API') {
                    setApiCredentialStatuses(prev => prev.map(status =>
                        status.key === updateModal.credentialKey
                            ? { ...status, isHealthy: true, hasValue: true, error: undefined }
                            : status
                    ));
                }

                setTimeout(() => {
                    closeUpdateModal();
                }, 1500);
            } else {
                setUpdateResult({ type: 'error', message: 'Failed to update credential' });
                flashbar.push({
                    type: 'error',
                    header: 'Credential update failed',
                    content: `Could not save ${updateModal.credentialName ?? 'this credential'}. Try again, or check the network tab for details.`,
                });
            }
        } catch (error) {
            setUpdateResult({ type: 'error', message: 'Error updating credential' });
            flashbar.push({
                type: 'error',
                header: 'Credential update failed',
                content: `An unexpected error occurred while saving ${updateModal.credentialName ?? 'this credential'}.`,
            });
        } finally {
            setUpdating(false);
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
            } catch (execErr) {
                console.error('Fallback: Unable to copy', execErr);
            }
            document.body.removeChild(textArea);
        }
    };

    interface ModalCopy {
        label: string;
        description: React.ReactNode;
        placeholder: string;
        constraintText?: string;
        inputType: 'password' | 'textarea';
    }

    const getCredentialModalCopy = (
        credentialKey?: CredentialType | Trading212CredentialUpdateRequestKeyEnum | string,
        provider?: 'SeekingAlpha' | 'Trading212' | 'API'
    ): ModalCopy => {
        if (provider === 'SeekingAlpha') {
            const isPro = credentialKey === CredentialType.SeekingAlphaPro;
            if (isPro) {
                return {
                    label: 'SeekingAlpha Pro session cookie',
                    description: (
                        <span>
                            Open the{' '}
                            <a
                                href="https://seekingalpha.com/pro-quant-portfolio/articles"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Quant Pro Portfolio page
                            </a>
                            , then in the browser&apos;s Network tab filter for{' '}
                            <strong>https://seekingalpha.com/api/v3/quant_pro_portfolio/articles</strong>{' '}
                            <Button
                                variant="inline-link"
                                iconName="copy"
                                onClick={() => copyToClipboard('https://seekingalpha.com/api/v3/quant_pro_portfolio/articles')}
                                ariaLabel="Copy network filter URL to clipboard"
                            />
                            {' '}and copy the Cookie request header.
                        </span>
                    ),
                    placeholder: 'eyJhbGciOi…',
                    constraintText: 'Looks like `eyJ…`. About 200 characters long.',
                    inputType: 'textarea',
                };
            }
            return {
                label: 'SeekingAlpha Premium session cookie',
                description: (
                    <span>
                        Open the{' '}
                        <a
                            href="https://seekingalpha.com/alpha-picks/articles"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Alpha Picks articles page
                        </a>
                        , then in the browser&apos;s Network tab filter for{' '}
                        <strong>https://seekingalpha.com/api/v3/service_plans/458/marketplace/articles</strong>{' '}
                        <Button
                            variant="inline-link"
                            iconName="copy"
                            onClick={() => copyToClipboard('https://seekingalpha.com/api/v3/service_plans/458/marketplace/articles')}
                            ariaLabel="Copy network filter URL to clipboard"
                        />
                        {' '}and copy the Cookie request header.
                    </span>
                ),
                placeholder: 'eyJhbGciOi…',
                constraintText: 'Looks like `eyJ…`. About 200 characters long.',
                inputType: 'textarea',
            };
        }
        if (provider === 'Trading212') {
            switch (credentialKey) {
                case 'Trading212Isa':
                    return {
                        label: 'Trading 212 ISA cookie',
                        description: (
                            <span>
                                In Trading 212 (Invest/ISA), open Dev Tools → Network → filter for{' '}
                                <strong>rest/v1/equity/order/instruments/settings</strong>{' '}
                                <Button
                                    variant="inline-link"
                                    iconName="copy"
                                    onClick={() => copyToClipboard('rest/v1/equity/order/instruments/settings')}
                                    ariaLabel="Copy endpoint to clipboard"
                                />
                                {' '}→ copy the Cookie request header.
                            </span>
                        ),
                        placeholder: 'TRADING212_SESSION=…',
                        constraintText: 'Full Cookie header value, including the `TRADING212_SESSION=` prefix.',
                        inputType: 'textarea',
                    };
                case 'Trading212Cfd':
                    return {
                        label: 'Trading 212 CFD cookie',
                        description: (
                            <span>
                                In Trading 212 (CFD), open Dev Tools → Network → filter for{' '}
                                <strong>instrumentarium/v1/instrument-trading-statuses</strong>{' '}
                                <Button
                                    variant="inline-link"
                                    iconName="copy"
                                    onClick={() => copyToClipboard('instrumentarium/v1/instrument-trading-statuses')}
                                    ariaLabel="Copy endpoint to clipboard"
                                />
                                {' '}→ copy the Cookie request header.
                            </span>
                        ),
                        placeholder: 'TRADING212_SESSION=…',
                        constraintText: 'Full Cookie header value, including the `TRADING212_SESSION=` prefix.',
                        inputType: 'textarea',
                    };
                case 'Trading212ApiKey':
                    return {
                        label: 'Trading 212 API key',
                        description: 'In Trading 212 (Invest/ISA), go to Settings → API → generate or copy the key.',
                        placeholder: 'XXXXXXXX-…',
                        constraintText: 'A short alphanumeric key issued by Trading 212.',
                        inputType: 'password',
                    };
                default:
                    return {
                        label: 'Trading 212 credential value',
                        description: 'Enter the credential value for this Trading 212 credential.',
                        placeholder: 'Paste credential here',
                        inputType: 'textarea',
                    };
            }
        }
        if (provider === 'API') {
            if (credentialKey === 'POLYGON_API_KEY') {
                return {
                    label: 'Polygon API key',
                    description: (
                        <span>
                            Sign in at{' '}
                            <a href="https://polygon.io/" target="_blank" rel="noopener noreferrer">
                                polygon.io
                            </a>{' '}
                            and copy your API key from the dashboard.
                        </span>
                    ),
                    placeholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxx',
                    constraintText: 'Roughly 32 characters; opaque token from your Polygon dashboard.',
                    inputType: PASSWORD_INPUT_KEYS.has(credentialKey as string) ? 'password' : 'textarea',
                };
            }
            if (credentialKey === 'ALPHA_WORKER_API_KEY') {
                return {
                    label: 'Alpha Worker API key',
                    description: 'Secret key used by the Claude Code alpha-analysis worker to authenticate against the TradeDigger API. Generate a strong random value (e.g. openssl rand -hex 32) and store the same value in your Claude Code secrets.',
                    placeholder: 'Paste key here',
                    constraintText: 'Any strong random string. Must match the ALPHA_WORKER_API_KEY secret in Claude Code.',
                    inputType: 'password',
                };
            }
            return {
                label: 'API key',
                description: 'Enter the API key value for this credential.',
                placeholder: 'Paste API key here',
                inputType: PASSWORD_INPUT_KEYS.has(credentialKey as string) ? 'password' : 'textarea',
            };
        }
        return {
            label: 'Credential value',
            description: 'Enter the credential value.',
            placeholder: 'Paste credential here',
            inputType: 'textarea',
        };
    };

    /** Returns a StatusIndicator for the card header — no section header wrapper. */
    const getStatusBadge = (status: CredentialHealthStatus): React.ReactNode => {
        if (!status.healthChecked) {
            return <StatusIndicator type="pending">Not checked</StatusIndicator>;
        }
        if (status.isHealthy) {
            return <StatusIndicator type="success">Healthy</StatusIndicator>;
        }
        return <StatusIndicator type="error">Unhealthy</StatusIndicator>;
    };

    /** Formats an ISO date string to a readable date, or returns "—". */
    const formatDate = (iso?: string): string => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    /**
     * Renders the inline buy-percentage control for SeekingAlpha cards.
     * Wrapped in an ExpandableSection so it's collapsed by default.
     */
    const renderBuyPctControl = (e: CredentialHealthStatus) => {
        if (!SEEKING_ALPHA_TYPES_WITH_BUY_PCT.has(e.key as string)) return null;

        const editor = getBuyPctEditor(e.key as string);
        const currentPct = e.buyPercentage;

        /** Change #6: quick-pick buttons only pre-fill the input; don't save immediately. */
        const handleQuickPick = (value: number) => {
            setBuyPctEditor(e.key as string, { inputValue: String(value), error: '' });
        };

        const handleFreeFormSave = () => {
            const parsed = parseFloat(editor.inputValue);
            if (isNaN(parsed)) {
                setBuyPctEditor(e.key as string, { error: 'Enter a number between 0 and 1.' });
                return;
            }
            if (parsed < 0 || parsed > 1) {
                setBuyPctEditor(e.key as string, { error: 'Value must be between 0.0 and 1.0.' });
                return;
            }
            void saveBuyPercentage(e.key as string, e.keyName, currentPct, parsed);
        };

        return (
            <ExpandableSection variant="inline" headerText="Adjust buy allocation">
                <SpaceBetween size="s">
                    <SpaceBetween direction="horizontal" size="xs">
                        {BUY_PCT_QUICK_PICKS.map(v => (
                            <Button
                                key={v}
                                variant="inline-link"
                                disabled={editor.saving}
                                onClick={() => handleQuickPick(v)}
                                data-testid={`buy-pct-quick-${e.key}-${v}`}
                                ariaLabel={`Pre-fill buy allocation to ${Math.round(v * 100)}%`}
                            >
                                {Math.round(v * 100)}%
                            </Button>
                        ))}
                    </SpaceBetween>
                    <FormField
                        errorText={editor.error || undefined}
                        constraintText="Range 0.0 – 1.0, step 0.05"
                    >
                        <SpaceBetween direction="horizontal" size="xs">
                            <Input
                                value={editor.inputValue}
                                onChange={({ detail }) =>
                                    setBuyPctEditor(e.key as string, { inputValue: detail.value, error: '' })
                                }
                                placeholder="0.50"
                                type="number"
                                step={0.05}
                                inputMode="decimal"
                                data-testid={`buy-pct-input-${e.key}`}
                            />
                            <Button
                                variant="primary"
                                loading={editor.saving}
                                onClick={handleFreeFormSave}
                                data-testid={`buy-pct-save-${e.key}`}
                            >
                                Save
                            </Button>
                        </SpaceBetween>
                    </FormField>
                </SpaceBetween>
            </ExpandableSection>
        );
    };

    const renderCredentialCards = (
        statuses: CredentialHealthStatus[],
        title: string,
        refreshFunction: () => void,
        isRefreshing: boolean,
        banner: { type: 'success' | 'error', message: string } | null,
        setBanner: (banner: { type: 'success' | 'error', message: string } | null) => void,
        sectionTestId?: string
    ) => (
        <SpaceBetween size="m">
            <Header
                variant="h2"
                actions={
                    <Button
                        iconName="refresh"
                        loading={isRefreshing}
                        onClick={refreshFunction}
                        variant="normal"
                        data-testid={sectionTestId ? `verify-section-${sectionTestId}` : undefined}
                    >
                        Verify
                    </Button>
                }
            >
                {title}
            </Header>

            {banner && (
                <Alert
                    type={banner.type}
                    dismissible
                    onDismiss={() => setBanner(null)}
                >
                    {banner.message}
                </Alert>
            )}

            <Cards
                ariaLabels={{
                    itemSelectionLabel: (e, n) => `select ${n.keyName}`,
                    selectionGroupLabel: "Credential selection"
                }}
                cardDefinition={{
                    /** Change #1: header shows credential name + status badge inline. */
                    header: (e: CredentialHealthStatus) => (
                        <SpaceBetween direction="horizontal" size="xs">
                            <Box fontWeight="bold">{e.keyName}</Box>
                            {getStatusBadge(e)}
                        </SpaceBetween>
                    ),
                    sections: [
                        /** Per-card success/error banner (Verify result). */
                        {
                            id: "banner",
                            content: (e: CredentialHealthStatus) => e.banner ? (
                                <Alert
                                    type={e.banner.type}
                                    dismissible
                                    onDismiss={() => {
                                        if (e.provider === 'SeekingAlpha') {
                                            setSeekingAlphaStatuses(prev => prev.map(status =>
                                                status.key === e.key
                                                    ? { ...status, banner: null }
                                                    : status
                                            ));
                                        } else if (e.provider === 'Trading212') {
                                            setTrading212Statuses(prev => prev.map(status =>
                                                status.key === e.key
                                                    ? { ...status, banner: null }
                                                    : status
                                            ));
                                        } else {
                                            setApiCredentialStatuses(prev => prev.map(status =>
                                                status.key === e.key
                                                    ? { ...status, banner: null }
                                                    : status
                                            ));
                                        }
                                    }}
                                >
                                    {e.banner.message}
                                </Alert>
                            ) : null
                        },
                        /**
                         * Change #4: error rendered inline under status area, no section
                         * header. Section is omitted entirely when there is no error.
                         * Change #3: section only included when there is an error to show.
                         */
                        ...(
                            // We can't filter per-item inside sections[], so we always
                            // include the error section but only render content when
                            // healthChecked AND an error exists.
                            [{
                                id: "error",
                                content: (e: CredentialHealthStatus) =>
                                    e.healthChecked && e.error ? (
                                        <Box color="text-status-error" fontSize="body-s">{e.error}</Box>
                                    ) : null
                            }]
                        ),
                        /**
                         * Change #2: metadata shown as KeyValuePairs.
                         * Change #3: section only included when there is metadata to show.
                         */
                        {
                            id: "meta",
                            content: (e: CredentialHealthStatus) => {
                                const hasMeta = e.updatedAt || SEEKING_ALPHA_TYPES_WITH_BUY_PCT.has(e.key as string);
                                if (!hasMeta) return null;

                                const items: Array<{ label: string; value: React.ReactNode }> = [];

                                if (e.updatedAt) {
                                    items.push({
                                        label: 'Last updated',
                                        value: formatDate(e.updatedAt),
                                    });
                                }

                                if (SEEKING_ALPHA_TYPES_WITH_BUY_PCT.has(e.key as string)) {
                                    items.push({
                                        label: 'Buy allocation',
                                        value: e.buyPercentage !== undefined
                                            ? `${Math.round(e.buyPercentage * 100)}%`
                                            : '—',
                                    });
                                }

                                return <KeyValuePairs items={items} />;
                            }
                        },
                        /**
                         * Change #5: buy allocation editor inside ExpandableSection.
                         * Change #3: only included for SeekingAlpha credential types.
                         */
                        {
                            id: "buyPercentage",
                            content: (e: CredentialHealthStatus) => renderBuyPctControl(e)
                        },
                        /**
                         * Setup instructions — only for Public API card.
                         * Change #3: null content means nothing renders.
                         */
                        {
                            id: "setupInstructions",
                            content: (e: CredentialHealthStatus) => (e.key === 'Trading212PublicApi' || e.key === 'Trading212PublicApiIna') ? (
                                <Box>
                                    <SpaceBetween size="xs">
                                        <Box variant="p">
                                            <strong>How to get your Trading212 API credentials:</strong>
                                        </Box>
                                        <ol style={{ margin: 0, paddingLeft: '1.2em' }}>
                                            <li>Open Trading212 &rarr; Settings &rarr; API (Beta)</li>
                                            <li>Accept the risk warning if prompted</li>
                                            <li>Click &ldquo;Generate new API key&rdquo;</li>
                                            <li>Give it a name and configure IP restrictions (recommended)</li>
                                            <li>Copy both the <strong>API Key</strong> and <strong>API Secret Key</strong> &mdash; the secret is shown only once</li>
                                        </ol>
                                        <Box variant="small" color="text-body-secondary">
                                            Available for General Invest and ISA accounts only.
                                        </Box>
                                    </SpaceBetween>
                                </Box>
                            ) : null
                        },
                        {
                            id: "actions",
                            content: (e: CredentialHealthStatus) => (
                                <SpaceBetween direction="horizontal" size="xs">
                                    <Button
                                        iconName="refresh"
                                        loading={e.isRefreshing}
                                        onClick={() => {
                                            if (e.provider === 'SeekingAlpha') {
                                                refreshIndividualSeekingAlphaCredential(e.key as CredentialType);
                                            } else if (e.provider === 'Trading212') {
                                                refreshIndividualTrading212Credential(e.key as Trading212CredentialUpdateRequestKeyEnum);
                                            } else {
                                                refreshIndividualApiCredential(e.key as string);
                                            }
                                        }}
                                        variant="normal"
                                    >
                                        Verify
                                    </Button>
                                    <Button
                                        variant="primary"
                                        iconName="edit"
                                        onClick={() => openUpdateModal(e)}
                                    >
                                        Edit
                                    </Button>
                                </SpaceBetween>
                            )
                        }
                    ]
                }}
                cardsPerRow={[
                    { cards: 1 },
                    { minWidth: 500, cards: 2 }
                ]}
                items={statuses}
                loadingText="Loading credentials..."
                empty={
                    <Box textAlign="center" color="inherit">
                        <b>No credentials found</b>
                        <Box
                            padding={{ bottom: "s" }}
                            variant="p"
                            color="inherit"
                        >
                            No credentials are currently configured.
                        </Box>
                    </Box>
                }
            />
        </SpaceBetween>
    );

    return (
        <Container>
            <SpaceBetween size="l">
                <Header
                    variant="h1"
                    actions={
                        <Button
                            iconName="refresh"
                            loading={refreshing}
                            onClick={refreshHealthStatus}
                        >
                            Refresh Status
                        </Button>
                    }
                >
                    Credentials Management
                </Header>

                {renderCredentialCards(
                    seekingAlphaStatuses,
                    "SeekingAlpha Credentials",
                    refreshSeekingAlphaHealth,
                    seekingAlphaRefreshing,
                    seekingAlphaBanner,
                    setSeekingAlphaBanner,
                    'seekingalpha'
                )}
                {renderCredentialCards(
                    trading212Statuses,
                    "Trading212 Credentials",
                    refreshTrading212Health,
                    trading212Refreshing,
                    trading212Banner,
                    setTrading212Banner,
                    'trading212'
                )}
                {renderCredentialCards(
                    apiCredentialStatuses,
                    "API Credentials",
                    refreshApiCredentialHealth,
                    apiCredentialRefreshing,
                    apiCredentialBanner,
                    setApiCredentialBanner,
                    'api'
                )}

                {/* Change #7: use password input for short-key credentials, textarea for cookies. */}
                <Modal
                    visible={updateModal.isVisible}
                    onDismiss={closeUpdateModal}
                    header={`Edit ${updateModal.credentialName ?? 'credential'}`}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={closeUpdateModal}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleUpdateCredential}
                                    loading={updating}
                                >
                                    Save
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        {updateResult && (
                            <Alert
                                type={updateResult.type}
                                dismissible
                                onDismiss={() => setUpdateResult(null)}
                            >
                                {updateResult.message}
                            </Alert>
                        )}

                        {(() => {
                            const copy = getCredentialModalCopy(updateModal.credentialKey, updateModal.provider);
                            return (
                                <FormField
                                    label={copy.label}
                                    description={copy.description}
                                    constraintText={copy.constraintText}
                                >
                                    {copy.inputType === 'password' ? (
                                        <Input
                                            type="password"
                                            value={updateModal.value}
                                            onChange={({ detail }) =>
                                                setUpdateModal(prev => ({ ...prev, value: detail.value }))
                                            }
                                            placeholder={copy.placeholder}
                                        />
                                    ) : (
                                        <Textarea
                                            value={updateModal.value}
                                            onChange={({ detail }) =>
                                                setUpdateModal(prev => ({ ...prev, value: detail.value }))
                                            }
                                            placeholder={copy.placeholder}
                                            rows={4}
                                        />
                                    )}
                                </FormField>
                            );
                        })()}
                    </SpaceBetween>
                </Modal>
                <Modal
                    visible={publicApiModal.isVisible}
                    onDismiss={closePublicApiModal}
                    header={publicApiModal.accountId === 'ina'
                        ? 'Edit Trading212 Public API credentials (Ina)'
                        : 'Edit Trading212 Public API credentials'}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={closePublicApiModal}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleUpdatePublicApiCredential}
                                    loading={publicApiUpdating}
                                >
                                    Save
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        {publicApiUpdateResult && (
                            <Alert
                                type={publicApiUpdateResult.type}
                                dismissible
                                onDismiss={() => setPublicApiUpdateResult(null)}
                            >
                                {publicApiUpdateResult.message}
                            </Alert>
                        )}
                        <FormField
                            label="API Key"
                            description="The API key generated from Trading212 Settings → API (Beta)."
                            constraintText="Leave blank to keep the existing key unchanged."
                        >
                            <Input
                                type="password"
                                value={publicApiModal.apiKey}
                                onChange={({ detail }) =>
                                    setPublicApiModal(prev => ({ ...prev, apiKey: detail.value }))
                                }
                                placeholder="Paste API key here"
                            />
                        </FormField>
                        <FormField
                            label="API Secret"
                            description="The API secret shown once when generating the key. Cannot be retrieved again."
                            constraintText="Leave blank to keep the existing secret unchanged."
                        >
                            <Input
                                type="password"
                                value={publicApiModal.apiSecret}
                                onChange={({ detail }) =>
                                    setPublicApiModal(prev => ({ ...prev, apiSecret: detail.value }))
                                }
                                placeholder="Paste API secret here"
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            </SpaceBetween>
        </Container>
    );
}
